import { createContext, useContext, type ReactNode } from 'react';
import type { DataTableClientBlock } from '@/types/ui-blocks';

/**
 * Context for injecting custom header content above data_table_client blocks.
 *
 * Used by source-specific report viewers (e.g., GoogleAdsReportViewer) to render
 * controls like date range pickers directly above the table — without modifying
 * shared block renderers or prop-drilling through the rendering chain.
 *
 * When no provider is present, the table renders normally with no addon.
 *
 * The renderer receives the block so providers can decide per-table whether to show
 * the addon (e.g., show date picker on campaign metrics and keyword tables).
 */
type TableBlockAddonRenderer = (block: DataTableClientBlock) => ReactNode | null;

const TableBlockAddonContext = createContext<TableBlockAddonRenderer | null>(null);

export function TableBlockAddonProvider({
  children,
  renderAddon,
}: {
  children: ReactNode;
  renderAddon: TableBlockAddonRenderer;
}) {
  return (
    <TableBlockAddonContext.Provider value={renderAddon}>
      {children}
    </TableBlockAddonContext.Provider>
  );
}

export function useTableBlockAddon(): TableBlockAddonRenderer | null {
  return useContext(TableBlockAddonContext);
}
